import React, { useState, useEffect } from 'react';
import { FaSearch, FaWhatsapp } from 'react-icons/fa';
import { InformePericial, CitaFormData, EstadoCita, ESTADO_CITA_ETIQUETAS, FaseInforme } from '../../types';
import { informeService } from '../../services/api';
import { showApiError } from '../../utils/toast';
import './EncargoForm.css'; // Reusing form styles

interface CitaFormProps {
    informe?: InformePericial;
    onUpdate: (informe: InformePericial, targetFase?: FaseInforme) => void;
}

const CitaForm: React.FC<CitaFormProps> = ({ informe, onUpdate }) => {
    const [formData, setFormData] = useState<CitaFormData>({
        fecha: '',
        hora: '',
        telefono: '',
        titulo: '',
        estado: EstadoCita.BUSCAR_FECHA
    });
    const [, setCita] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [exists, setExists] = useState(false);

    useEffect(() => {
        if (informe) {
            if (!formData.telefono && informe.telefono) {
                setFormData(prev => ({ ...prev, telefono: informe.telefono }));
            }
            prvCargarCita();
        }
    }, [informe]);

    useEffect(() => {
        if (informe?.numero_siniestro && formData.telefono) {
            setFormData(prev => ({
                ...prev,
                titulo: `Cita Siniestro ${informe.numero_siniestro} - ${formData.telefono}`
            }));
        }
    }, [informe, formData.telefono]);

    const prvCargarCita = async () => {
        if (!informe) return;
        try {
            const response = await informeService.obtenerCita(informe.id);
            if (response.data) {
                setCita(response.data);
                setFormData({
                    fecha: response.data.fecha ? response.data.fecha.split('T')[0] : '',
                    hora: response.data.hora || '',
                    telefono: response.data.telefono,
                    titulo: response.data.titulo,
                    estado: response.data.estado || EstadoCita.BUSCAR_FECHA
                });
                setExists(true);
            }
        } catch (err: any) {
            if (err.response?.status !== 404) {
                console.error('Error loading cita:', err);
            }
        }
    };

    const prvBuscarFecha = async () => {
        if (Number(formData.estado || EstadoCita.BUSCAR_FECHA) !== EstadoCita.BUSCAR_FECHA) {
            showApiError("La fecha solo se buscará si el estado está en 'Buscar Fecha en Calendario'");
            return;
        }

        if (!informe) {
            showApiError("Por favor guarda primero el encargo para buscar fecha en el calendario");
            return;
        }

        setLoading(true);
        try {
            const response = await informeService.buscarFechaLibre(informe.id);
            if (response.data && response.data.fecha && response.data.hora) {
                setFormData(prev => ({
                    ...prev,
                    fecha: response.data.fecha,
                    hora: response.data.hora,
                    estado: EstadoCita.PENDIENTE_CONFIRMAR
                }));
            }
        } catch (err: any) {
            console.error('Error al buscar fecha libre:', err);
        } finally {
            setLoading(false);
        }
    };

    const prvIniciarCitacion = async () => {
        if (!informe) return;
        setLoading(true);
        try {
            const response = await informeService.iniciarCitacion(informe.id);
            setCita(response.data);
            setFormData({
                fecha: response.data.fecha ? response.data.fecha.split('T')[0] : '',
                hora: response.data.hora || '',
                telefono: response.data.telefono,
                titulo: response.data.titulo,
                estado: response.data.estado || EstadoCita.MANDADO_MENSAJE
            });
            setExists(true);
            alert('Flujo de citación automática iniciado. Se ha reservado un hueco tentativo en Google Calendar y enviado la propuesta al asegurado por WhatsApp.');
        } catch (err: any) {
            console.error('Error starting citacion:', err);
        } finally {
            setLoading(false);
        }
    };

    const prvHandleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'estado' ? Number(value) : value
        }));
    };

    const prvGuardarCita = async () => {
        if (!informe) return;
        setLoading(true);
        try {
            let response;
            if (exists) {
                response = await informeService.actualizarCita(informe.id, formData);
            } else {
                response = await informeService.crearCita(informe.id, formData);
            }
            setCita(response.data);
            setExists(true);
            const msg = Number(formData.estado) === EstadoCita.CONFIRMAR 
                ? 'Cita guardada y registrada en Google Calendar correctamente.' 
                : 'Cita guardada correctamente.';
            alert(msg);
        } catch (err: any) {
            console.error('Error saving cita:', err);
            showApiError('Error al guardar la cita');
        } finally {
            setLoading(false);
        }
    };

    const prvHandleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!informe) return;

        if (Number(formData.estado) !== EstadoCita.CONFIRMAR) {
            showApiError("La cita debe estar en estado 'Confirmado' para pasar a la siguiente fase.");
            return;
        }

        setLoading(true);

        try {
            if (exists) {
                const response = await informeService.actualizarCita(informe.id, formData);
                setCita(response.data);
            } else {
                const response = await informeService.crearCita(informe.id, formData);
                setCita(response.data);
            }
            setExists(true);

            let updatedInforme = informe;
            try {
                const phaseRes = await informeService.cambiarFase(informe.id, 'videoperitacion');
                updatedInforme = phaseRes.data;
            } catch (e) {
                console.error('Error al actualizar fase en backend:', e);
            }

            onUpdate(updatedInforme, 'videoperitacion');
        } catch (err: any) {
            console.error('Error saving cita:', err);
        } finally {
            setLoading(false);
        }
    };

    const getEstadoStyles = (estado?: number | string) => {
        const est = Number(estado) || EstadoCita.BUSCAR_FECHA;
        switch (est) {
            case EstadoCita.BUSCAR_FECHA:
                return { backgroundColor: '#f0f9ff', color: '#0369a1', borderColor: '#bae6fd' };
            case EstadoCita.PENDIENTE_CONFIRMAR:
                return { backgroundColor: '#e0f2fe', color: '#0284c7', borderColor: '#7dd3fc' };
            case EstadoCita.MANDADO_MENSAJE:
                return { backgroundColor: '#bae6fd', color: '#0369a1', borderColor: '#38bdf8' };
            case EstadoCita.CONFIRMAR:
                return { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#60a5fa' };
            case EstadoCita.CANCELADO:
                return { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5' };
            default:
                return { backgroundColor: '#f0f9ff', color: '#0369a1', borderColor: '#bae6fd' };
        }
    };

    return (
        <form onSubmit={prvHandleSubmit} className="encargo-form">
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pt-2">
                <div>
                    <h3 className="section-title mb-1">Programación de Cita</h3>
                </div>
                <div className="flex gap-2 items-center">
                    <button type="button" onClick={prvGuardarCita} className="btn btn-secondary" disabled={loading} style={{ marginRight: '8px' }}>
                        Guardar Cita
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Guardando...' : 'Siguiente Fase'}
                    </button>
                </div>
            </div>

            <div className="form-grid">
                <div className="form-section">
                    <div className="form-row">
                        <div className="form-group">
                            <label>Fecha Cita</label>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="date"
                                    name="fecha"
                                    value={formData.fecha}
                                    onChange={prvHandleChange}
                                    className="input"
                                    style={{ paddingRight: formData.fecha ? '32px' : 'var(--spacing-sm)' }}
                                />
                                {formData.fecha && (
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, fecha: '' }))}
                                        title="Borrar fecha"
                                        style={{
                                            position: 'absolute',
                                            right: '8px',
                                            background: 'none',
                                            border: 'none',
                                            color: '#9ca3af',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            lineHeight: 1,
                                            padding: '2px 4px'
                                        }}
                                        onMouseOver={(e) => (e.currentTarget.style.color = '#ef4444')}
                                        onMouseOut={(e) => (e.currentTarget.style.color = '#9ca3af')}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Hora Cita</label>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="time"
                                    name="hora"
                                    value={formData.hora}
                                    onChange={prvHandleChange}
                                    className="input"
                                    style={{ paddingRight: formData.hora ? '32px' : 'var(--spacing-sm)' }}
                                />
                                {formData.hora && (
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, hora: '' }))}
                                        title="Borrar hora"
                                        style={{
                                            position: 'absolute',
                                            right: '8px',
                                            background: 'none',
                                            border: 'none',
                                            color: '#9ca3af',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            lineHeight: 1,
                                            padding: '2px 4px'
                                        }}
                                        onMouseOver={(e) => (e.currentTarget.style.color = '#ef4444')}
                                        onMouseOut={(e) => (e.currentTarget.style.color = '#9ca3af')}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="form-group" style={{ flex: 3, minWidth: '380px' }}>
                            <label>Estado Cita</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <select
                                    name="estado"
                                    value={formData.estado || EstadoCita.BUSCAR_FECHA}
                                    onChange={prvHandleChange}
                                    className="input"
                                    style={{
                                        ...getEstadoStyles(formData.estado),
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        padding: '8px 28px 8px 12px',
                                        borderRadius: '6px',
                                        minWidth: '380px',
                                        flex: 1
                                    }}
                                >
                                    <option value={EstadoCita.BUSCAR_FECHA} style={{ backgroundColor: '#f0f9ff', color: '#0369a1' }}>
                                        {ESTADO_CITA_ETIQUETAS[EstadoCita.BUSCAR_FECHA]}
                                    </option>
                                    <option value={EstadoCita.PENDIENTE_CONFIRMAR} style={{ backgroundColor: '#e0f2fe', color: '#0284c7' }}>
                                        {ESTADO_CITA_ETIQUETAS[EstadoCita.PENDIENTE_CONFIRMAR]}
                                    </option>
                                    <option value={EstadoCita.MANDADO_MENSAJE} style={{ backgroundColor: '#bae6fd', color: '#0369a1' }}>
                                        {ESTADO_CITA_ETIQUETAS[EstadoCita.MANDADO_MENSAJE]}
                                    </option>
                                    <option value={EstadoCita.CONFIRMAR} style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                                        {ESTADO_CITA_ETIQUETAS[EstadoCita.CONFIRMAR]}
                                    </option>
                                    <option value={EstadoCita.CANCELADO} style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
                                        {ESTADO_CITA_ETIQUETAS[EstadoCita.CANCELADO]}
                                    </option>
                                </select>
                                <button
                                    type="button"
                                    onClick={prvBuscarFecha}
                                    className="btn"
                                    title="Buscar fecha libre en Google Calendar"
                                    style={{
                                        backgroundColor: '#0284c7',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        padding: '9px 12px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '38px',
                                        flexShrink: 0
                                    }}
                                    disabled={loading}
                                >
                                    <FaSearch size={15} />
                                </button>
                                <button
                                    type="button"
                                    onClick={prvIniciarCitacion}
                                    className="btn"
                                    title="Pedir Cita por WhatsApp"
                                    style={{
                                        backgroundColor: '#25D366',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        padding: '9px 12px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '38px',
                                        flexShrink: 0
                                    }}
                                    disabled={loading}
                                >
                                    <FaWhatsapp size={19} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Teléfono Contacto *</label>
                            <input
                                type="tel"
                                name="telefono"
                                value={formData.telefono}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                    </div>

                    <div className="form-group full-width">
                        <label>Título de la Cita (Auto-generado)</label>
                        <input
                            type="text"
                            name="titulo"
                            value={formData.titulo}
                            onChange={prvHandleChange}
                            readOnly
                            className="input bg-gray-100"
                        />
                    </div>
                </div>
            </div>

        </form>
    );
};

export default CitaForm;
